import api from './api';
import API_ENDPOINTS from '../config/api';
import { ApiResponse } from '../utils/types';

export interface CreateSupportTicketData {
  subject: string;
  category:
    | 'Trading Issues'
    | 'Account Issues'
    | 'Payment Issues'
    | 'Technical Issues'
    | 'Other';
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  emailAddress: string;
  accountNumber?: string;
  issueDescription: string;
  attachments?: File[];
}

export interface UpdateSupportTicketData {
  category?:
    | 'Trading Issues'
    | 'Account Issues'
    | 'Payment Issues'
    | 'Technical Issues'
    | 'Other';
  issueDescription?: string;
  accountNumber?: string;
  attachments?: File[];
  existingAttachments?: any[];
}

export interface SupportTicket {
  id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  description: string;
  emailAddress: string;
  accountNumber?: string;
  createdAt: string;
  lastUpdated: string;
  replies?: number;
  attachments?: Array<{
    id: string;
    name: string;
    size: number;
    url: string;
    type: string;
  }>;
}

export const supportTicketAPI = {
  createTicket: async (
    data: CreateSupportTicketData
  ): Promise<ApiResponse<SupportTicket>> => {
    try {
      if (data.attachments && data.attachments.length > 2) {
        throw new Error('Maximum 2 files allowed');
      }

      if (data.attachments) {
        for (const file of data.attachments) {
          if (file.size > 5 * 1024 * 1024) {
            throw new Error(`File ${file.name} exceeds 5MB limit`);
          }
        }
      }

      const formData = new FormData();
      formData.append('subject', data.subject);
      formData.append('category', data.category);
      formData.append('priority', data.priority);
      formData.append('emailAddress', data.emailAddress);
      formData.append('issueDescription', data.issueDescription);
      if (data.accountNumber) {
        formData.append('accountNumber', data.accountNumber);
      }

      if (data.attachments && data.attachments.length > 0) {
        for (const file of data.attachments) {
          formData.append('attachments', file);
        }
      }

      const response = await api.post(
        API_ENDPOINTS.SUPPORT_TICKETS.CREATE,
        formData
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData;
      }

      return {
        success: false,
        error: responseData.message || 'Failed to create support ticket',
      };
    } catch (error: unknown) {
      console.error('Error creating support ticket:', error);

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string; error?: string } };
        };
        if (axiosError.response?.data) {
          throw new Error(
            axiosError.response.data.message ||
              axiosError.response.data.error ||
              'Failed to create support ticket'
          );
        }
      }

      throw error;
    }
  },

  updateTicket: async (
    ticketId: string,
    data: UpdateSupportTicketData
  ): Promise<ApiResponse<SupportTicket>> => {
    try {
      const formData = new FormData();

      if (data.category) {
        formData.append('category', data.category);
      }
      if (data.issueDescription) {
        formData.append('issueDescription', data.issueDescription);
      }
      if (data.accountNumber) {
        formData.append('accountNumber', data.accountNumber);
      }

      if (data.attachments && data.attachments.length > 0) {
        for (const file of data.attachments) {
          formData.append('attachments', file);
        }
      }

      if (data.existingAttachments && data.existingAttachments.length > 0) {
        for (const item of data.existingAttachments) {
          formData.append(
            'existingAttachments',
            typeof item === 'string' ? item : JSON.stringify(item)
          );
        }
      }

      const response = await api.put(
        API_ENDPOINTS.SUPPORT_TICKETS.USER_UPDATE_TICKET(ticketId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData;
      }

      return {
        success: false,
        error: responseData.message || 'Failed to update support ticket',
      };
    } catch (error: unknown) {
      console.error('Error updating support ticket:', error);

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string; error?: string } };
        };
        if (axiosError.response?.data) {
          throw new Error(
            axiosError.response.data.message ||
              axiosError.response.data.error ||
              'Failed to update support ticket'
          );
        }
      }

      throw error;
    }
  },

  getTickets: async (): Promise<ApiResponse<SupportTicket[]>> => {
    try {
      const response = await api.get(API_ENDPOINTS.SUPPORT_TICKETS.GET_ALL);
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData;
      }

      return {
        success: false,
        error: responseData.message || 'Failed to fetch support tickets',
      };
    } catch (error: unknown) {
      console.error('Error fetching support tickets:', error);

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string; error?: string } };
        };
        if (axiosError.response?.data) {
          throw new Error(
            axiosError.response.data.message ||
              axiosError.response.data.error ||
              'Failed to fetch support tickets'
          );
        }
      }

      throw error;
    }
  },

  getTicketById: async (
    ticketId: string
  ): Promise<ApiResponse<SupportTicket>> => {
    try {
      const response = await api.get(
        API_ENDPOINTS.SUPPORT_TICKETS.GET_BY_ID(ticketId)
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData;
      }

      return {
        success: false,
        error: responseData.message || 'Failed to fetch support ticket',
      };
    } catch (error: unknown) {
      console.error('Error fetching support ticket:', error);

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string; error?: string } };
        };
        if (axiosError.response?.data) {
          throw new Error(
            axiosError.response.data.message ||
              axiosError.response.data.error ||
              'Failed to fetch support ticket'
          );
        }
      }

      throw error;
    }
  },

  deleteTicket: async (
    ticketId: string
  ): Promise<ApiResponse<SupportTicket>> => {
    try {
      const response = await api.delete(
        API_ENDPOINTS.SUPPORT_TICKETS.DELETE(ticketId)
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData;
      }

      return {
        success: false,
        error: responseData.message || 'Failed to delete support ticket',
      };
    } catch (error: unknown) {
      console.error('Error deleting support ticket:', error);

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string; error?: string } };
        };
        if (axiosError.response?.data) {
          throw new Error(
            axiosError.response.data.message ||
              axiosError.response.data.error ||
              'Failed to delete support ticket'
          );
        }
      }

      throw error;
    }
  },
};

const supportTicketService = {
  createTicket: supportTicketAPI.createTicket,
  updateTicket: supportTicketAPI.updateTicket,
  getTickets: supportTicketAPI.getTickets,
  getTicketById: supportTicketAPI.getTicketById,
  deleteTicket: supportTicketAPI.deleteTicket,
};

export default supportTicketService;
